import manifest from '@/config/grid-view-presets.json'

import type {
  GridViewPayload,
  GridViewPresetContext,
  GridViewSystemView,
} from '../types'
import { getGridViewPayloadVersion } from './payload'

export type ManifestPreset = {
  key: string
  scopeKey: string
  surfaces?: string[]
  name: string
  description?: string | null
  priority: number
  context?: {
    projectProfiles?: string[]
  }
  columns?: string[]
  hiddenColumns?: string[]
  columnSizing?: GridViewPayload['columnSizing']
  sort?: Array<{
    colId: string
    sort: 'asc' | 'desc'
    sortIndex?: number
  }>
}

const presetMatchesSurface = (
  preset: ManifestPreset,
  context: GridViewPresetContext | undefined,
) => {
  const surfaces = preset.surfaces ?? []
  return (
    surfaces.length === 0 ||
    Boolean(context?.surface && surfaces.includes(context.surface))
  )
}

const getActiveProjectProfile = (context: GridViewPresetContext | undefined) =>
  context?.projectProfile ?? context?.reportingProfile

const contextPriority = (
  preset: ManifestPreset,
  context: GridViewPresetContext | undefined,
) => {
  const projectProfiles = preset.context?.projectProfiles ?? []
  if (projectProfiles.length === 0) return 1

  const activeProfile = getActiveProjectProfile(context)
  if (activeProfile && projectProfiles.includes(activeProfile)) return 2

  return 0
}

export const buildPresetPayload = ({
  preset,
  basePayload,
}: {
  preset: ManifestPreset
  basePayload: GridViewPayload
}): GridViewPayload => {
  const visibleColumnIds = preset.columns ?? []
  const hiddenColumnIds = new Set(preset.hiddenColumns ?? [])
  const hasPresetSort = (preset.sort?.length ?? 0) > 0
  const hasPresetSizing = (preset.columnSizing?.columns.length ?? 0) > 0
  if (
    visibleColumnIds.length === 0 &&
    hiddenColumnIds.size === 0 &&
    !hasPresetSort &&
    !hasPresetSizing
  ) {
    return basePayload
  }

  const columnsById = new Map(
    basePayload.columns.map((column) => [column.colId, column]),
  )
  const knownColumnIds = new Set(columnsById.keys())
  const explicitVisible = new Set(visibleColumnIds)
  const orderedVisibleColumns = visibleColumnIds.flatMap((colId) => {
    const column = columnsById.get(colId)
    if (!column) return []
    return [
      {
        ...column,
        hidden: hiddenColumnIds.has(colId) ? true : false,
      },
    ]
  })
  const remainingColumns = basePayload.columns
    .filter((column) => !explicitVisible.has(column.colId))
    .map((column) => ({
      ...column,
      ...(hiddenColumnIds.has(column.colId)
        ? { hidden: true }
        : visibleColumnIds.length > 0
          ? { hidden: true }
          : {}),
    }))
  const presetSort =
    preset.sort
      ?.flatMap((entry) => {
        if (!knownColumnIds.has(entry.colId)) return []
        if (entry.sort !== 'asc' && entry.sort !== 'desc') return []

        return [
          {
            colId: entry.colId,
            sort: entry.sort,
            ...(typeof entry.sortIndex === 'number'
              ? { sortIndex: entry.sortIndex }
              : {}),
          },
        ]
      })
      .sort((left, right) => {
        if (left.sortIndex == null && right.sortIndex == null) {
          return left.colId.localeCompare(right.colId)
        }
        if (left.sortIndex == null) return 1
        if (right.sortIndex == null) return -1
        return left.sortIndex - right.sortIndex
      }) ?? []

  return {
    ...basePayload,
    columns: [...orderedVisibleColumns, ...remainingColumns],
    sort: presetSort.length > 0 ? presetSort : basePayload.sort,
    ...(preset.columnSizing ? { columnSizing: preset.columnSizing } : {}),
  }
}

export const getSystemGridViewManifest = () => manifest

export const resolveSystemGridViewPresets = ({
  scopeKey,
  gridSchemaVersion,
  presetContext,
  basePayload,
}: {
  scopeKey: string
  gridSchemaVersion: string
  presetContext?: GridViewPresetContext
  basePayload: GridViewPayload
}): GridViewSystemView[] => {
  const baseGridSchemaVersion =
    gridSchemaVersion.split('+')[0] ?? gridSchemaVersion

  if (
    manifest.payloadVersion !== getGridViewPayloadVersion() ||
    manifest.gridSchemaVersion !== baseGridSchemaVersion
  ) {
    return []
  }

  return (manifest.presets as ManifestPreset[])
    .filter((preset) => preset.scopeKey === scopeKey)
    .filter((preset) => presetMatchesSurface(preset, presetContext))
    .sort((left, right) => {
      const contextDelta =
        contextPriority(right, presetContext) -
        contextPriority(left, presetContext)
      if (contextDelta !== 0) return contextDelta
      if (right.priority !== left.priority)
        return right.priority - left.priority
      return left.key.localeCompare(right.key)
    })
    .map((preset, index) => ({
      key: preset.key,
      name: preset.name,
      description: preset.description ?? null,
      scopeKey: preset.scopeKey,
      payload: buildPresetPayload({ preset, basePayload }),
      isDefault: index === 0,
      isCompatible: true,
    }))
}
